/**
 * @fileoverview Navigation Guard Context
 * Prevents in-app navigation when there are unsaved changes.
 * Pages register their guard function; the Sidebar checks before navigating.
 */
import { createContext, useContext, useRef, useCallback } from 'react';

const NavigationGuardContext = createContext({
    registerGuard: () => { },
    unregisterGuard: () => { },
    canNavigate: () => true,
});

export const useNavigationGuard = () => useContext(NavigationGuardContext);

export const NavigationGuardProvider = ({ children }) => {
    // guardRef holds a function that returns true if navigation should be BLOCKED
    const guardRef = useRef(null);

    const registerGuard = useCallback((guardFn) => {
        guardRef.current = guardFn;
    }, []);

    const unregisterGuard = useCallback(() => {
        guardRef.current = null;
    }, []);

    /**
     * Returns true if navigation is allowed, false if blocked.
     * If blocked, shows a confirmation dialog.
     */
    const canNavigate = useCallback((message) => {
        if (guardRef.current && guardRef.current()) {
            // There are unsaved changes — ask user
            return window.confirm(message || '¿Tenés cambios sin guardar. ¿Querés salir sin guardar?');
        }
        return true; // no guard or no unsaved changes
    }, []);

    return (
        <NavigationGuardContext.Provider value={{ registerGuard, unregisterGuard, canNavigate }}>
            {children}
        </NavigationGuardContext.Provider>
    );
};
